import { useTheme } from "@mui/material";
import { blend } from "@mui/system";
import { useTranslation } from "react-i18next";
import { useEntityProps } from "../utilsHooks";

enum Priorities {
  LOWEST = 0,
  LOW = 10,
  MIDDLE = 20,
  HIGH = 30,
  HIGHEST = 40,
}

/**
 * Single source of truth for how a priority looks in the app: label and color.
 *
 * Use these getters wherever a priority is shown.
 * Never hardcode its label and color in a component, change them here.
 * Every value is shown in the "Entity Identity/Priority" story.
 */
export const usePriority = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const priorities = {
    [Priorities.LOWEST]: {
      label: t("Priority.priorityLowest"),
      color: theme.palette.custom.levelsColor.lowest,
    },
    [Priorities.LOW]: {
      label: t("Priority.priorityLow"),
      color: theme.palette.custom.levelsColor.low,
    },
    [Priorities.MIDDLE]: {
      label: t("Priority.priorityMiddle"),
      color: theme.palette.custom.levelsColor.middle,
    },
    [Priorities.HIGH]: {
      label: t("Priority.priorityHigh"),
      color: theme.palette.custom.levelsColor.high,
    },
    [Priorities.HIGHEST]: {
      label: t("Priority.priorityHighest"),
      color: theme.palette.custom.levelsColor.highest,
    },
  } as Record<Priorities, { label: string; color: string }>;
  const getProp = useEntityProps(priorities);

  const getPriorityLabel = getProp<string>("label");
  const getPriorityColor = getProp<string>("color");
  const getPriorityOptions = () =>
    Object.entries(priorities).map(([val, props]) => ({ value: parseInt(val), ...props }));

  const isNamedPriority = (val: any): val is number =>
    Object.keys(priorities)
      .map((n) => parseInt(n))
      .includes(val);

  const getTwoClosestPriority = (priorityNumber: number): [Priorities, Priorities] => {
    const priorityValues = Object.values(Priorities)
      .map(Number)
      .sort((a, b) => a - b);

    // If the priority number matches exactly, return that color twice
    if (priorityValues.includes(priorityNumber)) {
      return [priorityNumber as Priorities, priorityNumber as Priorities];
    }

    // Find the two closest values
    let lowerValue = priorityValues[0];
    let upperValue = priorityValues[priorityValues.length - 1];

    for (const value of priorityValues) {
      if (value <= priorityNumber) {
        lowerValue = value;
      }
      if (value >= priorityNumber && value < upperValue) {
        upperValue = value;
      }
    }

    // Handle edge cases
    if (priorityNumber < priorityValues[0]) {
      // Below the lowest priority, return the two lowest colors
      return [priorityValues[0] as Priorities, priorityValues[1] as Priorities];
    }

    if (priorityNumber > priorityValues[priorityValues.length - 1]) {
      // Above the highest priority, return the two highest colors
      return [
        priorityValues[priorityValues.length - 2] as Priorities,
        priorityValues[priorityValues.length - 1] as Priorities,
      ];
    }

    // Return the two closest colors (lower and upper)
    return [lowerValue as Priorities, upperValue as Priorities];
  };

  const getColorForCustomPriority = (priority: number) => {
    const [first, second] = getTwoClosestPriority(priority);
    return blend(getPriorityColor(first), getPriorityColor(second), (priority - first) / (second - first));
  };

  return { getPriorityLabel, getPriorityColor, getPriorityOptions, isNamedPriority, getColorForCustomPriority };
};
